import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Tabs,
  Tag,
  Popconfirm,
  Upload,
  Alert,
  Statistic,
  Progress,
  Tooltip,
  Badge,
  Radio,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const BASE_URL = '/api/v1/card-maker';

// 稀有度颜色：自由文本，按哈希取色
const RARITY_COLOR_POOL = ['#8c8c8c', '#1677ff', '#722ed1', '#faad14', '#52c41a', '#ff4d4f', '#eb2f96', '#13c2c2'];
const getRarityColor = (v: string) => {
  if (!v) return '#8c8c8c';
  let hash = 0;
  for (let i = 0; i < v.length; i++) hash = v.charCodeAt(i) + ((hash << 5) - hash);
  return RARITY_COLOR_POOL[Math.abs(hash) % RARITY_COLOR_POOL.length];
};

interface CardSet {
  id: number;
  code: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  created: string;
}

interface CardItem {
  id: number;
  cardSetId: number;
  cardNumber: string;
  name: string;
  thumbnailUrl?: string;
  rarity: string;
  description?: string;
}

interface CardSku {
  id: number;
  cardSetId: number;
  code: string;
  name: string;
  packSize: number;
  thumbnailUrl?: string;
  description?: string;
}

interface SkuRatio {
  id: number;
  skuId: number;
  cardId: number;
  quantity: number;
  card: CardItem;
}

interface SkuRule {
  id: number;
  skuId: number;
  rarity: string;
  operator: string; // 'exactly' | 'at_least' | 'at_most'
  count: number;
}

interface Batch {
  id: number;
  skuId: number;
  totalPacks: number;
  completedPacks: number;
  status: string;
  notes?: string;
  created: string;
}

interface Pack {
  id: number;
  batchId: number;
  packNumber: number;
  cardIds: number[];
  cardNumbers: string[];
  isProduced: boolean;
}

const OPERATOR_LABELS: Record<string, string> = {
  exactly: '恰好',
  at_least: '至少',
  at_most: '最多',
};

// ==================== Card Sets Tab ====================

const CardSetsTab: React.FC<{ onSetChange: () => void }> = ({ onSetChange }) => {
  const [sets, setSets] = useState<CardSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSet, setEditingSet] = useState<CardSet | null>(null);
  const [form] = Form.useForm();
  const [setThumbUploading, setSetThumbUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/sets`);
      const json = await res.json();
      if (json.success) setSets(json.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingSet(null); form.resetFields(); setModalVisible(true); };
  const openEdit = (record: CardSet) => { setEditingSet(record); form.setFieldsValue(record); setModalVisible(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editingSet ? `${BASE_URL}/sets/${editingSet.id}` : `${BASE_URL}/sets`;
      const method = editingSet ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) {
        message.success(editingSet ? '更新成功' : '创建成功');
        setModalVisible(false);
        load();
        onSetChange();
      } else {
        message.error(json.message || '操作失败');
      }
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/sets/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); load(); onSetChange(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '编号', dataIndex: 'code', width: 100 },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 100,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name' },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '创建时间', dataIndex: 'created', width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作', width: 120,
      render: (_: any, record: CardSet) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="卡牌系列管理"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增系列</Button>
        </Space>
      }
    >
      <Table rowKey="id" dataSource={sets} columns={columns} loading={loading} pagination={{ pageSize: 20 }} />
      <Modal
        title={editingSet ? '编辑系列' : '新增系列'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="code" label="系列编号" rules={[{ required: true }]}>
            <Input placeholder="如: CM001" />
          </Form.Item>
          <Form.Item name="name" label="系列名称" rules={[{ required: true }]}>
            <Input placeholder="如: 深海巨兽系列" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图">
            <Input placeholder="https://..." style={{ marginBottom: 8 }} />
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.thumbnailUrl !== cur.thumbnailUrl || prev.code !== cur.code}>
              {({ getFieldValue, setFieldValue }) => {
                const url = getFieldValue('thumbnailUrl');
                return (
                  <Space align="start">
                    {url && <img src={`${url}?x-oss-process=image/resize,w_200`} alt="缩略图" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }} />}
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        const code = getFieldValue('code');
                        if (!code) { message.warning('请先填写系列编号'); return false; }
                        setSetThumbUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('setCode', code);
                          fd.append('file', file);
                          const res = await fetch(`${BASE_URL}/sets/upload-thumbnail`, { method: 'POST', body: fd });
                          const json = await res.json();
                          if (json.success) {
                            setFieldValue('thumbnailUrl', json.data.url);
                            message.success('上传成功');
                          } else {
                            message.error(json.message);
                          }
                        } catch (e: any) {
                          message.error(e.message);
                        } finally {
                          setSetThumbUploading(false);
                        }
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={setThumbUploading} size="small">上传图片</Button>
                    </Upload>
                  </Space>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ==================== Cards Tab ====================

interface CardWithInventory extends CardItem {
  stock: number;
  totalConsumed: number;
  monthlyConsumption: number;
  monthsRemaining: number | null;
  skuMinCoverage: number;
  isBelowSkuMin: boolean;
  isLowStock: boolean;
  isCriticalStock: boolean;
  restockSuggestion: number;
}

interface ImportRow {
  cardNumber: string;
  name: string;
  rarity: string;
  thumbnailUrl: string;
  description: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

const CardsTab: React.FC<{ sets: CardSet[] }> = ({ sets }) => {
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [cards, setCards] = useState<CardWithInventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCard, setEditingCard] = useState<CardItem | null>(null);
  const [form] = Form.useForm();

  // 库存设置（单张）
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [stockCard, setStockCard] = useState<CardWithInventory | null>(null);
  const [stockMode, setStockMode] = useState<'set' | 'add'>('set');
  const [stockForm] = Form.useForm();

  // 批量库存设置
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchMode, setBatchMode] = useState<'set' | 'add'>('add');
  const [batchValues, setBatchValues] = useState<Record<number, number | undefined>>({});
  const [batchNotes, setBatchNotes] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  // 按稀有度快速填写：rarity -> 临时输入值
  const [rarityQuickValues, setRarityQuickValues] = useState<Record<string, number | undefined>>({});

  // Excel import state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState(false);

  // 批量缩略图上传
  const [thumbModalVisible, setThumbModalVisible] = useState(false);
  const [thumbFiles, setThumbFiles] = useState<File[]>([]);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbProgress, setThumbProgress] = useState(0);
  interface ThumbResult { filename: string; cardNumber: string; status: 'success' | 'not_found' | 'error'; url?: string; error?: string; }
  const [thumbResults, setThumbResults] = useState<ThumbResult[]>([]);

  const load = useCallback(async () => {
    if (!selectedSetId) return;
    setLoading(true);
    try {
      // 优先使用带库存统计的接口
      try {
        const res = await fetch(`${BASE_URL}/card-inventory?setId=${selectedSetId}`);
        const json = await res.json();
        if (json.success) {
          setCards(json.data);
          return;
        }
      } catch { /* 降级处理 */ }

      // 降级：仅加载卡牌基础数据（新表迁移前或接口异常时）
      try {
        const res = await fetch(`${BASE_URL}/cards?setId=${selectedSetId}`);
        const json = await res.json();
        if (json.success) {
          setCards(json.data.map((c: CardItem) => ({
            ...c,
            stock: 0, totalConsumed: 0,
            monthlyConsumption: 0, monthsRemaining: null,
            skuMinCoverage: 0, isBelowSkuMin: false,
            isLowStock: false, isCriticalStock: false, restockSuggestion: 0,
          })));
        } else {
          message.error(json.message || '加载失败');
        }
      } catch (e: any) {
        message.error(`加载卡牌失败：${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSetId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingCard(null);
    form.resetFields();
    form.setFieldsValue({ cardSetId: selectedSetId });
    setModalVisible(true);
  };
  const openEdit = (record: CardItem) => { setEditingCard(record); form.setFieldsValue(record); setModalVisible(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editingCard ? `${BASE_URL}/cards/${editingCard.id}` : `${BASE_URL}/cards`;
      const method = editingCard ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalVisible(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/cards/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  // ---- 单张库存设置 ----
  const openStockModal = (card: CardWithInventory) => {
    setStockCard(card);
    setStockMode('set');
    stockForm.setFieldsValue({ stock: card.stock, delta: 0, notes: '' });
    setStockModalVisible(true);
  };

  const handleStockSubmit = async (values: { stock: number; delta: number; notes: string }) => {
    if (!stockCard) return;
    const newStock = stockMode === 'add'
      ? Math.max(0, stockCard.stock + (values.delta || 0))
      : values.stock;
    try {
      const res = await fetch(`${BASE_URL}/card-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: stockCard.id, stock: newStock, notes: values.notes }),
      });
      const json = await res.json();
      if (json.success) { message.success('库存更新成功'); setStockModalVisible(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  // ---- 批量库存设置 ----
  const openBatchModal = () => {
    setBatchMode('add');
    setBatchValues({});
    setBatchNotes('');
    setRarityQuickValues({});
    setBatchModalVisible(true);
  };

  const applyRarityQuickFill = (rarity: string) => {
    const val = rarityQuickValues[rarity];
    if (val === undefined || val === null) return;
    const updates: Record<number, number> = {};
    cards.filter(c => c.rarity === rarity).forEach(c => { updates[c.id] = val; });
    setBatchValues(prev => ({ ...prev, ...updates }));
  };

  const handleBatchSave = async () => {
    const entries = Object.entries(batchValues).filter(([, v]) => v !== undefined && v !== null) as [string, number][];
    if (entries.length === 0) { message.warning('未修改任何库存值'); return; }
    setBatchLoading(true);
    let successCount = 0;
    for (const [cardIdStr, val] of entries) {
      const cardId = parseInt(cardIdStr);
      const card = cards.find(c => c.id === cardId);
      if (!card) continue;
      const newStock = batchMode === 'add' ? Math.max(0, card.stock + val) : Math.max(0, val);
      try {
        const res = await fetch(`${BASE_URL}/card-inventory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cardId, stock: newStock, notes: batchNotes || undefined }),
        });
        const json = await res.json();
        if (json.success) successCount++;
      } catch {}
    }
    setBatchLoading(false);
    message.success(`批量更新完成：${successCount}/${entries.length} 成功`);
    setBatchModalVisible(false);
    load();
  };

  // ---- Excel 模板下载 ----
  const downloadTemplate = () => {
    const templateData = [
      ['卡牌编号', '卡牌名称', '稀有度', '缩略图URL', '描述'],
      ['001', '示例卡A', 'N', 'https://example.com/a.jpg', '这是一张普通卡'],
      ['002', '示例卡B', 'R', 'https://example.com/b.jpg', '这是一张稀有卡'],
      ['003', '示例卡C', 'SR', 'https://example.com/c.jpg', '这是一张超稀有卡'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    // 设置列宽
    ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 40 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '卡牌模板');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), '卡牌批量导入模板.xlsx');
  };

  // ---- Excel 文件解析 ----
  const handleFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];

        if (rows.length < 2) { message.error('文件内容为空'); return; }

        const headers: string[] = rows[0].map((h: any) => String(h).trim());
        const colIdx = (name: string) => headers.indexOf(name);

        const numIdx    = colIdx('卡牌编号');
        const nameIdx   = colIdx('卡牌名称');
        const rarityIdx = colIdx('稀有度');
        const thumbIdx  = colIdx('缩略图URL');
        const descIdx   = colIdx('描述');

        if (numIdx === -1 || nameIdx === -1) {
          message.error('文件格式错误，必须包含「卡牌编号」和「卡牌名称」列');
          return;
        }

        const parsed: ImportRow[] = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const num  = String(row[numIdx]  ?? '').trim();
          const name = String(row[nameIdx] ?? '').trim();
          if (!num && !name) continue; // 跳过空行
          parsed.push({
            cardNumber:   num,
            name:         name,
            rarity:       rarityIdx !== -1 ? String(row[rarityIdx] ?? '').trim() : '',
            thumbnailUrl: thumbIdx  !== -1 ? String(row[thumbIdx]  ?? '').trim() : '',
            description:  descIdx   !== -1 ? String(row[descIdx]   ?? '').trim() : '',
            status: 'pending',
          });
        }

        if (parsed.length === 0) { message.error('未解析到有效数据'); return; }

        // 检测文件内重复编号
        const seenInFile = new Map<string, number>(); // cardNumber -> first row index
        for (let i = 0; i < parsed.length; i++) {
          const num = parsed[i].cardNumber;
          if (!num) {
            parsed[i] = { ...parsed[i], status: 'error', error: '卡牌编号不能为空' };
            continue;
          }
          if (seenInFile.has(num)) {
            const firstIdx = seenInFile.get(num)!;
            if (parsed[firstIdx].status !== 'error') {
              parsed[firstIdx] = { ...parsed[firstIdx], status: 'error', error: '文件内编号重复' };
            }
            parsed[i] = { ...parsed[i], status: 'error', error: `编号重复（与第 ${firstIdx + 2} 行相同）` };
          } else {
            seenInFile.set(num, i);
          }
        }

        // 检测与现有卡牌的编号冲突
        const existingNumbers = new Set(cards.map(c => c.cardNumber));
        for (let i = 0; i < parsed.length; i++) {
          if (parsed[i].status === 'error') continue;
          if (existingNumbers.has(parsed[i].cardNumber)) {
            parsed[i] = { ...parsed[i], status: 'error', error: '编号已存在于当前系列' };
          }
        }

        const errorCount = parsed.filter(r => r.status === 'error').length;
        setImportRows(parsed);
        setImportDone(false);
        setImportProgress(0);
        setImportModalVisible(true);
        if (errorCount > 0) {
          message.warning(`解析完成：共 ${parsed.length} 条，其中 ${errorCount} 条编号冲突已标记`);
        } else {
          message.success(`解析成功，共 ${parsed.length} 条`);
        }
      } catch {
        message.error('文件解析失败，请检查格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止默认上传
  };

  // ---- 批量导入执行 ----
  const handleImport = async () => {
    if (!selectedSetId) return;
    setImporting(true);
    setImportProgress(0);
    const rows = [...importRows];
    const preSkipped = rows.filter(r => r.status === 'error').length;
    const toProcess = rows.filter(r => r.status === 'pending').length;
    let processed = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.status === 'error') continue; // 跳过重复/校验失败行
      try {
        const res = await fetch(`${BASE_URL}/cards`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cardSetId:    selectedSetId,
            cardNumber:   row.cardNumber,
            name:         row.name,
            rarity:       row.rarity || 'N',
            thumbnailUrl: row.thumbnailUrl || undefined,
            description:  row.description || undefined,
          }),
        });
        const json = await res.json();
        rows[i] = { ...row, status: json.success ? 'success' : 'error', error: json.success ? undefined : json.message };
      } catch (e: any) {
        rows[i] = { ...row, status: 'error', error: e.message };
      }
      processed++;
      setImportProgress(toProcess > 0 ? Math.round((processed / toProcess) * 100) : 100);
      setImportRows([...rows]);
    }

    setImporting(false);
    setImportDone(true);
    const successCount = rows.filter(r => r.status === 'success').length;
    const apiErrors = rows.filter(r => r.status === 'error').length - preSkipped;
    let msg = `导入完成：成功 ${successCount} 条`;
    if (preSkipped > 0) msg += `，跳过重复 ${preSkipped} 条`;
    if (apiErrors > 0) msg += `，失败 ${apiErrors} 条`;
    message.success(msg);
    load();
  };

  const handleBatchThumbUpload = async () => {
    if (!selectedSetId || thumbFiles.length === 0) return;
    setThumbUploading(true);
    setThumbProgress(0);
    setThumbResults([]);
    const results: ThumbResult[] = [];
    for (let i = 0; i < thumbFiles.length; i++) {
      const file = thumbFiles[i];
      try {
        const formData = new FormData();
        formData.append('setId', String(selectedSetId));
        formData.append('files', file);
        const res = await fetch(`${BASE_URL}/cards/batch-upload-thumbnails`, { method: 'POST', body: formData });
        const json = await res.json();
        if (json.success && json.data.length > 0) {
          results.push(json.data[0]);
        } else {
          results.push({ filename: file.name, cardNumber: '', status: 'error', error: json.message });
        }
      } catch (e: any) {
        results.push({ filename: file.name, cardNumber: '', status: 'error', error: e.message });
      }
      setThumbProgress(Math.round(((i + 1) / thumbFiles.length) * 100));
      setThumbResults([...results]);
    }
    setThumbUploading(false);
    const successCount = results.filter(r => r.status === 'success').length;
    message.success(`上传完成：${successCount}/${results.length} 成功`);
    load();
  };

  const importColumns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    { title: '稀有度', dataIndex: 'rarity', width: 80 },
    { title: '缩略图URL', dataIndex: 'thumbnailUrl', ellipsis: true },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v: string, row: ImportRow) => {
        if (v === 'success') return <Tag color="green">成功</Tag>;
        if (v === 'error')   return <Tooltip title={row.error}><Tag color="red">失败</Tag></Tooltip>;
        return <Tag>待导入</Tag>;
      },
    },
  ];

  const columns = [
    { title: '编号', dataIndex: 'cardNumber', width: 70 },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 70,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name', width: 130 },
    {
      title: '稀有度', dataIndex: 'rarity', width: 80,
      render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
    },
    {
      title: '库存(张)', dataIndex: 'stock', width: 90,
      render: (v: number, r: CardWithInventory) => (
        <span
          className={r.isCriticalStock ? 'stock-critical' : r.isLowStock ? 'stock-warning' : 'stock-ok'}
          style={{ fontWeight: 600, cursor: 'pointer' }}
          onClick={() => openStockModal(r)}
        >
          {v}
        </span>
      ),
    },
    {
      title: '月均消耗', dataIndex: 'monthlyConsumption', width: 80,
      render: (v: number) => v > 0 ? v : <Text type="secondary">-</Text>,
    },
    {
      title: 'SKU需求', dataIndex: 'skuMinCoverage', width: 80,
      render: (v: number, r: CardWithInventory) => {
        if (!v) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title="生产本系列所有SKU各一轮所需最低库存">
            <span className={r.isBelowSkuMin ? 'stock-warning' : ''}>{v}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '预计余量', dataIndex: 'monthsRemaining', width: 90,
      render: (v: number | null, r: CardWithInventory) => {
        if (v === null) return <Text type="secondary">-</Text>;
        return (
          <span className={r.isCriticalStock ? 'stock-critical' : r.isLowStock ? 'stock-warning' : 'stock-ok'}>
            {v.toFixed(1)} 月
          </span>
        );
      },
    },
    {
      title: '状态', width: 100,
      render: (_: any, r: CardWithInventory) => {
        if (r.isCriticalStock) return <Tag color="red">严重不足</Tag>;
        if (r.isBelowSkuMin && r.monthlyConsumption === 0) return <Tag color="orange">低于SKU下限</Tag>;
        if (r.isLowStock) return <Tag color="orange">需补货</Tag>;
        if (r.monthlyConsumption === 0 && !r.isBelowSkuMin) return <Tag>无消耗</Tag>;
        return <Tag color="green">充足</Tag>;
      },
    },
    {
      title: '建议补货', dataIndex: 'restockSuggestion', width: 90,
      render: (v: number) => v > 0 ? <Text type="danger" strong>{v} 张</Text> : '-',
    },
    {
      title: '操作', width: 130,
      render: (_: any, record: CardWithInventory) => (
        <Space>
          <Button size="small" onClick={() => openStockModal(record)}>库存</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const successCount = importRows.filter(r => r.status === 'success').length;
  const errorCount   = importRows.filter(r => r.status === 'error').length;

  return (
    <Card
      title="卡牌管理"
      extra={
        <Space>
          <Select
            placeholder="选择系列"
            style={{ width: 200 }}
            value={selectedSetId}
            onChange={setSelectedSetId}
            options={sets.map(s => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          />
          <Button icon={<ReloadOutlined />} onClick={load} disabled={!selectedSetId}>刷新</Button>
          <Button onClick={openBatchModal} disabled={!selectedSetId || cards.length === 0}>批量入库</Button>
          <Button icon={<UploadOutlined />} onClick={() => { setThumbModalVisible(true); setThumbFiles([]); setThumbResults([]); }} disabled={!selectedSetId || cards.length === 0}>批量上传缩略图</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={!selectedSetId}>下载模板</Button>
          <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleFileSelect} disabled={!selectedSetId}>
            <Button icon={<FileExcelOutlined />} disabled={!selectedSetId}>导入Excel</Button>
          </Upload>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!selectedSetId}>新增卡牌</Button>
        </Space>
      }
    >
      {!selectedSetId && <Alert message="请先选择卡牌系列" type="info" showIcon />}
      {selectedSetId && (() => {
        const critical = cards.filter(c => c.isCriticalStock);
        const belowSkuOnly = cards.filter(c => !c.isCriticalStock && c.isBelowSkuMin && c.monthlyConsumption === 0);
        const low = cards.filter(c => c.isLowStock && !c.isCriticalStock && !belowSkuOnly.includes(c));
        return (
          <>
            {critical.length > 0 && (
              <Alert type="error" showIcon icon={<WarningOutlined />} style={{ marginBottom: 12 }}
                message={`${critical.length} 张卡库存严重不足：${critical.map(c => `${c.cardNumber} ${c.name}(建议补${c.restockSuggestion}张)`).join('、')}`}
              />
            )}
            {belowSkuOnly.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${belowSkuOnly.length} 张卡低于SKU最低保障量（不足以生产所有SKU各一轮）：${belowSkuOnly.map(c => `${c.cardNumber}(需${c.skuMinCoverage}张，缺${c.restockSuggestion}张)`).join('、')}`}
              />
            )}
            {low.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${low.length} 张卡库存不足1个月均消耗：${low.map(c => `${c.cardNumber}(建议补${c.restockSuggestion}张)`).join('、')}`}
              />
            )}
            <Table rowKey="id" dataSource={cards} columns={columns} loading={loading}
              pagination={{ pageSize: 50 }}
              rowClassName={(r: CardWithInventory) => r.isCriticalStock ? 'ant-table-row-danger' : r.isLowStock ? 'ant-table-row-warn' : ''}
            />
          </>
        );
      })()}

      {/* 单条新增/编辑 Modal */}
      <Modal
        title={editingCard ? '编辑卡牌' : '新增卡牌'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="cardSetId" hidden><Input /></Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="cardNumber" label="卡牌编号" rules={[{ required: true }]}>
                <Input placeholder="001" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="卡牌名称" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="rarity" label="稀有度" extra="可自由填写，如：N、R、SR、SSR、UR 等">
            <Input placeholder="如: N / R / SR / SSR / UR" />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 库存设置 Modal（单张） */}
      <Modal
        title={stockCard ? `库存操作 — ${stockCard.cardNumber} ${stockCard.name}` : '库存操作'}
        open={stockModalVisible}
        onCancel={() => setStockModalVisible(false)}
        onOk={() => stockForm.submit()}
        okText="保存"
      >
        {stockCard && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}><Statistic title="当前库存" value={stockCard.stock} suffix="张" /></Col>
            <Col span={8}><Statistic title="月均消耗" value={stockCard.monthlyConsumption || '-'} suffix={stockCard.monthlyConsumption ? '张' : ''} /></Col>
            <Col span={8}><Statistic title="累计消耗" value={stockCard.totalConsumed} suffix="张" /></Col>
          </Row>
        )}
        <Radio.Group
          value={stockMode}
          onChange={e => { setStockMode(e.target.value); stockForm.resetFields(['stock', 'delta']); }}
          style={{ marginBottom: 16 }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="set">直接设置</Radio.Button>
          <Radio.Button value="add">追加入库</Radio.Button>
        </Radio.Group>
        <Form form={stockForm} layout="vertical" onFinish={handleStockSubmit}>
          {stockMode === 'set' ? (
            <Form.Item name="stock" label="设置库存数量（张）" rules={[{ required: true }]}
              extra="直接填写当前实际库存张数，系统将以此为准">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item
              name="delta"
              label="追加数量（张）"
              rules={[{ required: true }]}
              extra={stockCard
                ? `当前 ${stockCard.stock} + 追加 = 新库存`
                : '填写本次入库张数，将累加到当前库存'}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="备注（入库原因等）">
            <Input.TextArea rows={2} placeholder="如：2026-02 入库 500 张" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量库存设置 Modal */}
      {(() => {
        const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));
        const changedCount = Object.values(batchValues).filter(v => v !== undefined).length;
        const batchColumns = [
          { title: '编号', dataIndex: 'cardNumber', width: 70 },
          { title: '名称', dataIndex: 'name' },
          {
            title: '稀有度', dataIndex: 'rarity', width: 80,
            render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
          },
          {
            title: '当前库存', dataIndex: 'stock', width: 90,
            render: (v: number) => <Text type="secondary">{v} 张</Text>,
          },
          {
            title: batchMode === 'add' ? '追加数量' : '设置为',
            width: 140,
            render: (_: any, r: CardWithInventory) => (
              <InputNumber
                min={0}
                size="small"
                style={{ width: '100%' }}
                value={batchValues[r.id]}
                placeholder="不修改"
                onChange={val => setBatchValues(prev => ({ ...prev, [r.id]: val ?? undefined }))}
              />
            ),
          },
          {
            title: '操作后库存', width: 100,
            render: (_: any, r: CardWithInventory) => {
              const v = batchValues[r.id];
              if (v === undefined) return <Text type="secondary">不变</Text>;
              const result = batchMode === 'add' ? r.stock + v : v;
              return <Text strong style={{ color: result < r.stock ? '#ff4d4f' : '#52c41a' }}>{result} 张</Text>;
            },
          },
        ];
        return (
          <Modal
            title="批量库存操作"
            open={batchModalVisible}
            onCancel={() => setBatchModalVisible(false)}
            width={760}
            footer={
              <Space>
                <Button onClick={() => setBatchModalVisible(false)}>取消</Button>
                <Button
                  type="primary"
                  loading={batchLoading}
                  disabled={changedCount === 0}
                  onClick={handleBatchSave}
                >
                  保存 {changedCount > 0 ? `（${changedCount} 张）` : ''}
                </Button>
              </Space>
            }
          >
            <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Text strong>操作方式：</Text>
              </Col>
              <Col>
                <Radio.Group
                  value={batchMode}
                  onChange={e => { setBatchMode(e.target.value); setBatchValues({}); }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="add">追加入库</Radio.Button>
                  <Radio.Button value="set">直接设置</Radio.Button>
                </Radio.Group>
              </Col>
            </Row>

            {uniqueRarities.length > 0 && (
              <>
                <Divider orientation="left" plain style={{ margin: '0 0 12px' }}>按稀有度快速填写</Divider>
                <Row gutter={[12, 8]} style={{ marginBottom: 16 }}>
                  {uniqueRarities.map(rarity => (
                    <Col key={rarity}>
                      <Space size={4}>
                        <Tag color={getRarityColor(rarity)} style={{ margin: 0, minWidth: 36, textAlign: 'center' }}>{rarity}</Tag>
                        <InputNumber
                          min={0}
                          size="small"
                          style={{ width: 90 }}
                          placeholder="数量"
                          value={rarityQuickValues[rarity]}
                          onChange={val => setRarityQuickValues(prev => ({ ...prev, [rarity]: val ?? undefined }))}
                        />
                        <Button
                          size="small"
                          type="dashed"
                          onClick={() => applyRarityQuickFill(rarity)}
                          disabled={rarityQuickValues[rarity] === undefined}
                        >
                          填入全部 {rarity}
                        </Button>
                      </Space>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <Divider orientation="left" plain style={{ margin: '0 0 12px' }}>逐张调整</Divider>
            <Table
              rowKey="id"
              dataSource={cards}
              columns={batchColumns}
              size="small"
              pagination={{ pageSize: 20 }}
              scroll={{ y: 320 }}
            />

            <div style={{ marginTop: 12 }}>
              <Text type="secondary">备注（可选）：</Text>
              <Input.TextArea
                rows={2}
                style={{ marginTop: 4 }}
                placeholder="如：2026-02 批量入库"
                value={batchNotes}
                onChange={e => setBatchNotes(e.target.value)}
              />
            </div>
          </Modal>
        );
      })()}

      {/* Excel 批量导入 Modal */}
      <Modal
        title="Excel 批量导入卡牌"
        open={importModalVisible}
        onCancel={() => { if (!importing) { setImportModalVisible(false); } }}
        width={800}
        footer={
          importDone ? (
            <Button type="primary" onClick={() => setImportModalVisible(false)}>关闭</Button>
          ) : (
            <Space>
              <Button onClick={() => setImportModalVisible(false)} disabled={importing}>取消</Button>
              <Button type="primary" onClick={handleImport} loading={importing} icon={<FileExcelOutlined />}>
                确认导入 {importRows.filter(r => r.status === 'pending').length} 条
              </Button>
            </Space>
          )
        }
      >
        {importing && (
          <Progress percent={importProgress} style={{ marginBottom: 16 }} />
        )}
        {importDone && (
          <Alert
            type={errorCount > 0 ? 'warning' : 'success'}
            message={`导入完成：成功 ${successCount} 条，失败 ${errorCount} 条`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Table
          rowKey={(r, i) => String(i)}
          dataSource={importRows}
          columns={importColumns}
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* 批量缩略图上传 Modal */}
      <Modal
        title="批量上传卡牌缩略图"
        open={thumbModalVisible}
        onCancel={() => { if (!thumbUploading) setThumbModalVisible(false); }}
        width={680}
        footer={
          <Space>
            <Button onClick={() => setThumbModalVisible(false)} disabled={thumbUploading}>关闭</Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={thumbUploading}
              disabled={thumbFiles.length === 0}
              onClick={handleBatchThumbUpload}
            >
              开始上传 {thumbFiles.length > 0 ? `（${thumbFiles.length} 张）` : ''}
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="图片文件名即为卡牌编号（如 001.jpg → 编号 001），上传后自动匹配更新对应卡牌的缩略图 URL。"
        />
        <Upload.Dragger
          multiple
          accept="image/*"
          showUploadList={{ showRemoveIcon: true }}
          fileList={thumbFiles.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' as const, size: f.size, type: f.type }))}
          beforeUpload={(_, fileList) => {
            setThumbFiles(prev => {
              const existing = new Set(prev.map(f => f.name));
              const newFiles = fileList.filter(f => !existing.has(f.name));
              return [...prev, ...newFiles];
            });
            return false;
          }}
          onRemove={file => {
            setThumbFiles(prev => prev.filter(f => f.name !== file.name));
          }}
          disabled={thumbUploading}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">拖拽或点击选择图片（可多选）</p>
          <p className="ant-upload-hint">支持 JPG、PNG、WebP 等格式，文件名需与卡牌编号对应</p>
        </Upload.Dragger>

        {thumbUploading && (
          <Progress percent={thumbProgress} style={{ marginTop: 12 }} status="active" />
        )}

        {thumbResults.length > 0 && (
          <Table
            style={{ marginTop: 16 }}
            rowKey="filename"
            dataSource={thumbResults}
            size="small"
            pagination={false}
            scroll={{ y: 240 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              { title: '编号', dataIndex: 'cardNumber', width: 80 },
              {
                title: '结果', dataIndex: 'status', width: 100,
                render: (s: string, r: ThumbResult) => {
                  if (s === 'success') return <Tag color="green">成功</Tag>;
                  if (s === 'not_found') return <Tag color="orange">未匹配</Tag>;
                  return <Tooltip title={r.error}><Tag color="red">失败</Tag></Tooltip>;
                },
              },
            ]}
          />
        )}
      </Modal>
    </Card>
  );
};

// ==================== SKU Tab ====================

const SkuTab: React.FC<{ sets: CardSet[] }> = ({ sets }) => {
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [skus, setSkus] = useState<CardSku[]>([]);
  const [cards, setCards] = useState<CardItem[]>([]);
  const [ratios, setRatios] = useState<SkuRatio[]>([]);
  const [loading, setLoading] = useState(false);
  const [skuModalVisible, setSkuModalVisible] = useState(false);
  const [editingSku, setEditingSku] = useState<CardSku | null>(null);
  const [selectedSku, setSelectedSku] = useState<CardSku | null>(null);
  const [skuForm] = Form.useForm();
  const [ratioQuickValues, setRatioQuickValues] = useState<Record<string, number | undefined>>({});
  const [ratioQuickLoading, setRatioQuickLoading] = useState(false);
  const [localQuantities, setLocalQuantities] = useState<Record<number, number>>({});
  const [rules, setRules] = useState<SkuRule[]>([]);
  const [ruleForm] = Form.useForm();
  const [skuThumbUploading, setSkuThumbUploading] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchPacksModal, setBatchPacksModal] = useState<{ batch: Batch; packs: Pack[] } | null>(null);
  const [batchPacksLoading, setBatchPacksLoading] = useState(false);
  const [selectedPackKeys, setSelectedPackKeys] = useState<number[]>([]);
  const [stickerGenerating, setStickerGenerating] = useState(false);
  const [rarityOrder, setRarityOrder] = useState<string[]>([]);

  // 默认稀有度排序：R < SR < SSR < UR < 其他（字母序）
  const DEFAULT_RARITY_ORDER = ['R', 'SR', 'SSR', 'UR'];

  const loadSkus = useCallback(async () => {
    if (!selectedSetId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/skus?setId=${selectedSetId}`);
      const json = await res.json();
      if (json.success) setSkus(json.data);
    } finally { setLoading(false); }
  }, [selectedSetId]);

  const loadCards = useCallback(async () => {
    if (!selectedSetId) return;
    const res = await fetch(`${BASE_URL}/cards?setId=${selectedSetId}`);
    const json = await res.json();
    if (json.success) setCards(json.data);
  }, [selectedSetId]);

  const loadRatios = useCallback(async (skuId: number) => {
    const res = await fetch(`${BASE_URL}/ratios?skuId=${skuId}`);
    const json = await res.json();
    if (json.success) setRatios(json.data);
  }, []);

  useEffect(() => { loadSkus(); loadCards(); }, [loadSkus, loadCards]);

  // 当 cards 变化时，初始化稀有度排序（已有自定义顺序则保留，新稀有度追加到末尾）
  useEffect(() => {
    if (cards.length === 0) return;
    const cardRarities = Array.from(new Set(cards.map(c => c.rarity || '未知')));
    // 检查是否有新的稀有度
    const currentSet = new Set(rarityOrder);
    const hasNew = cardRarities.some(r => !currentSet.has(r));
    const hasRemoved = rarityOrder.some(r => !cardRarities.includes(r));
    if (hasNew || hasRemoved || rarityOrder.length === 0) {
      // 按默认顺序排列，未知稀有度按字母序追加
      const ordered = [...cardRarities].sort((a, b) => {
        const ia = DEFAULT_RARITY_ORDER.indexOf(a);
        const ib = DEFAULT_RARITY_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b);
      });
      // 保留用户已有的顺序，只追加新的
      const kept = rarityOrder.filter(r => ordered.includes(r));
      const newOnes = ordered.filter(r => !currentSet.has(r));
      setRarityOrder([...kept, ...newOnes]);
    }
  }, [cards]); // intentionally exclude rarityOrder to avoid reset loop

  // 每次 ratios 刷新时同步本地数量
  useEffect(() => {
    const map: Record<number, number> = {};
    ratios.forEach(r => { map[r.cardId] = r.quantity; });
    setLocalQuantities(map);
  }, [ratios]);

  const handleSkuSubmit = async (values: any) => {
    try {
      const url = editingSku ? `${BASE_URL}/skus/${editingSku.id}` : `${BASE_URL}/skus`;
      const method = editingSku ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...values, cardSetId: selectedSetId }) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setSkuModalVisible(false); loadSkus(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleQuantityChange = async (cardId: number, value: number) => {
    if (!selectedSku) return;
    const ratio = ratios.find(r => r.cardId === cardId);
    try {
      if (value > 0) {
        await fetch(`${BASE_URL}/ratios`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skuId: selectedSku.id, cardId, quantity: value }),
        });
      } else if (ratio) {
        await fetch(`${BASE_URL}/ratios/${ratio.id}`, { method: 'DELETE' });
      }
    } catch {}
    loadRatios(selectedSku.id);
  };

  const handleDeleteSku = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/skus/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); loadSkus(); if (selectedSku?.id === id) { setSelectedSku(null); setRatios([]); } }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const loadRules = useCallback(async (skuId: number) => {
    const res = await fetch(`${BASE_URL}/sku-rules?skuId=${skuId}`);
    const json = await res.json();
    if (json.success) setRules(json.data);
  }, []);

  const loadBatches = useCallback(async (skuId: number) => {
    const res = await fetch(`${BASE_URL}/batches?skuId=${skuId}`);
    const json = await res.json();
    if (json.success) setBatches(json.data);
  }, []);

  const openBatchPacks = async (batch: Batch) => {
    setBatchPacksLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/batches/${batch.id}/packs`);
      const json = await res.json();
      if (json.success) setBatchPacksModal({ batch, packs: json.data });
      else message.error(json.message);
    } finally {
      setBatchPacksLoading(false);
    }
  };

  const handleUpsertRule = async (values: any) => {
    if (!selectedSku) return;
    const res = await fetch(`${BASE_URL}/sku-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skuId: selectedSku.id, ...values }),
    });
    const json = await res.json();
    if (json.success) {
      ruleForm.resetFields();
      loadRules(selectedSku.id);
    } else {
      message.error(json.message);
    }
  };

  const handleDeleteRule = async (id: number) => {
    await fetch(`${BASE_URL}/sku-rules/${id}`, { method: 'DELETE' });
    if (selectedSku) loadRules(selectedSku.id);
  };

  const handleRarityRatioFill = async (rarity: string) => {
    if (!selectedSku) return;
    const qty = ratioQuickValues[rarity];
    if (qty === undefined || qty === null || qty <= 0) return;
    const targets = cards.filter(c => c.rarity === rarity);
    if (targets.length === 0) return;
    setRatioQuickLoading(true);
    let count = 0;
    for (const card of targets) {
      try {
        const res = await fetch(`${BASE_URL}/ratios`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skuId: selectedSku.id, cardId: card.id, quantity: qty }),
        });
        const json = await res.json();
        if (json.success) count++;
      } catch {}
    }
    setRatioQuickLoading(false);
    message.success(`已为 ${count} 张「${rarity}」卡设置数量 ${qty}`);
    loadRatios(selectedSku.id);
  };

  const totalCardsInBatch = ratios.reduce((sum, r) => sum + r.quantity, 0);
  const maxPacksFull = selectedSku && selectedSku.packSize > 0 ? Math.floor(totalCardsInBatch / selectedSku.packSize) : 0;
  const maxPacksRemainder = selectedSku && selectedSku.packSize > 0 ? totalCardsInBatch % selectedSku.packSize : 0;

  // ==================== 贴纸PDF生成 ====================

  const buildStickerHTML = (
    sku: CardSku,
    set: CardSet,
    cards: CardItem[],
    skuRatios: SkuRatio[],
  ): string => {
    // 构建数量映射
    const qtyMap = new Map<number, number>();
    skuRatios.forEach(r => qtyMap.set(r.cardId, r.quantity));

    // 按稀有度分组并统计
    const rarityCards = new Map<string, CardItem[]>();
    const rarityQty = new Map<string, number>();

    cards.forEach(c => {
      const r = c.rarity || '未知';
      if (!rarityCards.has(r)) {
        rarityCards.set(r, []);
      }
      rarityCards.get(r)!.push(c);
      const qty = qtyMap.get(c.id) ?? 0;
      rarityQty.set(r, (rarityQty.get(r) ?? 0) + qty);
    });

    // 使用用户配置的稀有度排序，只保留有数据的
    const activeRarities = rarityOrder.filter(r =>
      (rarityCards.get(r)?.length ?? 0) > 0 && (rarityQty.get(r) ?? 0) > 0
    );

    const totalQty = Array.from(rarityQty.values()).reduce((a, b) => a + b, 0);
    const cardKindCount = cards.filter(c => (qtyMap.get(c.id) ?? 0) > 0).length;

    // 标题
    const titleHTML = `<div class="sticker-title">${set.name}${sku.name}</div>`;

    // 副标题
    const subtitleHTML = `<div class="sticker-subtitle">本卡牌包含${cardKindCount}种卡牌，分成${activeRarities.length}种稀有度。</div>`;

    // 表格
    let tableHTML = '<table class="sticker-table"><thead><tr><th>稀有度</th>';
    activeRarities.forEach(r => {
      tableHTML += `<th>${r}</th>`;
    });
    tableHTML += '</tr></thead><tbody>';

    // 种类行
    tableHTML += '<tr><td>种类</td>';
    activeRarities.forEach(r => {
      tableHTML += `<td>${rarityCards.get(r)?.length ?? 0}</td>`;
    });
    tableHTML += '</tr>';

    // 获得概率行
    tableHTML += '<tr><td>获得概率</td>';
    activeRarities.forEach(r => {
      const qty = rarityQty.get(r) ?? 0;
      const prob = totalQty > 0 ? (qty / totalQty * 100).toFixed(1) : '0.0';
      tableHTML += `<td>${prob}%</td>`;
    });
    tableHTML += '</tr></tbody></table>';

    // 警告
    const warningHTML = `
      <div class="sticker-warning">
        <div class="sticker-warning-title">&#x26A0; 警告</div>
        <div>警告！包含细小零件。请勿给36月及以下的婴幼儿玩耍。</div>
      </div>`;

    // 生产信息
    const now = new Date();
    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const productionHTML = `
      <div class="sticker-production">
        <div>生产日期：${yearMonth}</div>
        <div>执行标准：FCIX2606-03</div>
        <div>制造商：金华创享创意设计有限责任公司</div>
      </div>`;

    // 角落信息
    const cornerHTML = `
      <div class="sticker-corner">
        <span>年龄：8+</span>
        <span>${sku.code}x10包</span>
      </div>`;

    return `<div class="sticker-page">${titleHTML}${subtitleHTML}${tableHTML}${warningHTML}${productionHTML}${cornerHTML}</div>`;
  };

  const captureStickerCanvas = async (
    sku: CardSku,
    set: CardSet,
    cards: CardItem[],
    skuRatios: SkuRatio[],
  ): Promise<HTMLCanvasElement> => {
    // 查找或创建隐藏渲染容器
    let renderDiv = document.getElementById('sticker-render-target') as HTMLDivElement | null;
    if (!renderDiv) {
      renderDiv = document.createElement('div');
      renderDiv.id = 'sticker-render-target';
      document.body.appendChild(renderDiv);
    }

    // 设置贴纸HTML
    renderDiv.innerHTML = buildStickerHTML(sku, set, cards, skuRatios);

    // 等待字体渲染
    await new Promise(r => setTimeout(r, 150));

    // 用html2canvas捕获
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(renderDiv.firstElementChild as HTMLElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    // 清理
    renderDiv.innerHTML = '';

    return canvas;
  };

  const generateSingleStickerPDF = async (sku: CardSku) => {
    const set = sets.find(s => s.id === selectedSetId);
    if (!set || !selectedSetId) return;

    setStickerGenerating(true);
    message.loading({ content: '正在生成贴纸PDF...', key: 'sticker-pdf' });

    try {
      // 获取该SKU的配比
      let skuRatios: SkuRatio[] = ratios;
      if (selectedSku?.id !== sku.id) {
        const res = await fetch(`${BASE_URL}/ratios?skuId=${sku.id}`);
        const json = await res.json();
        if (json.success) skuRatios = json.data;
      }

      const canvas = await captureStickerCanvas(sku, set, cards, skuRatios);

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        unit: 'mm',
        format: [60, 60],
        hotfixes: ['px_scaling'],
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 60, 60);

      const pdfBlob = pdf.output('blob');
      const fileName = `贴纸_${set.name}_${sku.code}.pdf`;
      saveAs(pdfBlob, fileName);

      message.success({ content: '贴纸PDF已生成', key: 'sticker-pdf' });
    } catch (e: any) {
      message.error({ content: `生成失败: ${e.message}`, key: 'sticker-pdf' });
    } finally {
      setStickerGenerating(false);
    }
  };

  const generateAllStickersPDF = async () => {
    const set = sets.find(s => s.id === selectedSetId);
    if (!set || !selectedSetId || skus.length === 0) return;

    setStickerGenerating(true);
    message.loading({ content: `正在生成 ${skus.length} 个SKU贴纸...`, key: 'sticker-pdf' });

    try {
      const { jsPDF } = await import('jspdf');
      const JSZip = (await import('jszip')).default;

      const zip = new JSZip();

      for (const sku of skus) {
        // 获取每个SKU的配比
        const res = await fetch(`${BASE_URL}/ratios?skuId=${sku.id}`);
        const json = await res.json();
        const skuRatios: SkuRatio[] = json.success ? json.data : [];

        const canvas = await captureStickerCanvas(sku, set, cards, skuRatios);

        const pdf = new jsPDF({
          unit: 'mm',
          format: [60, 60],
          hotfixes: ['px_scaling'],
        });

        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 60, 60);
        const pdfArrayBuffer = pdf.output('arraybuffer');

        zip.file(`贴纸_${set.name}_${sku.code}.pdf`, pdfArrayBuffer, { binary: true });
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `贴纸_${set.name}_全部_${skus.length}款.zip`;
      saveAs(zipBlob, zipFileName);

      message.success({ content: `已生成 ${skus.length} 款贴纸（ZIP压缩包）`, key: 'sticker-pdf' });
    } catch (e: any) {
      message.error({ content: `生成失败: ${e.message}`, key: 'sticker-pdf' });
    } finally {
      setStickerGenerating(false);
    }
  };

  const skuColumns = [
    { title: '编码', dataIndex: 'code', width: 90, render: (v: string) => v || '-' },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 70,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name' },
    { title: '每包张数', dataIndex: 'packSize', width: 100 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '操作', width: 160,
      render: (_: any, record: CardSku) => (
        <Space>
          <Button size="small" onClick={() => { setSelectedSku(record); setRatioQuickValues({}); setLocalQuantities({}); setRules([]); setBatches([]); loadRatios(record.id); loadRules(record.id); loadBatches(record.id); }}>配置比例</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingSku(record); skuForm.setFieldsValue(record); setSkuModalVisible(true); }} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDeleteSku(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ratioColumns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '稀有度', dataIndex: 'rarity', width: 80,
      render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
    },
    {
      title: '数量', dataIndex: 'id', width: 110,
      render: (cardId: number) => (
        <InputNumber
          min={0}
          size="small"
          style={{ width: 80 }}
          value={localQuantities[cardId] ?? 0}
          onChange={val => setLocalQuantities(prev => ({ ...prev, [cardId]: val ?? 0 }))}
          onBlur={() => handleQuantityChange(cardId, localQuantities[cardId] ?? 0)}
          onPressEnter={() => handleQuantityChange(cardId, localQuantities[cardId] ?? 0)}
        />
      ),
    },
    {
      title: '状态', dataIndex: 'id', width: 80,
      render: (cardId: number) => (localQuantities[cardId] ?? 0) > 0
        ? <Tag color="blue">已包含</Tag>
        : <Tag>未包含</Tag>,
    },
  ];

  return (
    <Card title="SKU管理">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Select
            placeholder="选择系列"
            style={{ width: 200 }}
            value={selectedSetId}
            onChange={v => { setSelectedSetId(v); setSelectedSku(null); setRatios([]); }}
            options={sets.map(s => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          />
        </Col>
      </Row>

      {!selectedSetId && <Alert message="请先选择卡牌系列" type="info" showIcon />}

      {selectedSetId && rarityOrder.length > 0 && (
        <Card size="small" title="稀有度排序（贴纸表格列顺序）" style={{ marginBottom: 12 }}>
          <Space wrap>
            {rarityOrder.map((r, i) => (
              <Tag
                key={r}
                color={getRarityColor(r)}
                closable={false}
                style={{ margin: 2, padding: '2px 6px', fontSize: 13 }}
              >
                {i > 0 && (
                  <Button
                    type="text"
                    size="small"
                    style={{ padding: 0, marginRight: 4, fontSize: 10, minWidth: 16 }}
                    onClick={() => {
                      setRarityOrder(prev => {
                        const next = [...prev];
                        [next[i - 1], next[i]] = [next[i], next[i - 1]];
                        return next;
                      });
                    }}
                  >◀</Button>
                )}
                {r}
                {i < rarityOrder.length - 1 && (
                  <Button
                    type="text"
                    size="small"
                    style={{ padding: 0, marginLeft: 4, fontSize: 10, minWidth: 16 }}
                    onClick={() => {
                      setRarityOrder(prev => {
                        const next = [...prev];
                        [next[i], next[i + 1]] = [next[i + 1], next[i]];
                        return next;
                      });
                    }}
                  >▶</Button>
                )}
              </Tag>
            ))}
          </Space>
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>点击 ◀ ▶ 调整稀有度在贴纸表格中的列顺序</Text>
          </div>
        </Card>
      )}

      {selectedSetId && (
        <Row gutter={16}>
          <Col span={10}>
            <Card
              size="small"
              title="SKU列表"
              extra={
                <Space>
                  <Button icon={<ReloadOutlined />} size="small" onClick={loadSkus} />
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={generateAllStickersPDF}
                    loading={stickerGenerating}
                    disabled={skus.length === 0}
                  >
                    生成全部贴纸
                  </Button>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditingSku(null); skuForm.resetFields(); setSkuModalVisible(true); }}>新增SKU</Button>
                </Space>
              }
            >
              <Table rowKey="id" dataSource={skus} columns={skuColumns} loading={loading} pagination={false} size="small"
                rowClassName={r => r.id === selectedSku?.id ? 'ant-table-row-selected' : ''}
              />
            </Card>
          </Col>
          <Col span={14}>
            {selectedSku ? (
              <Card
                size="small"
                title={`「${selectedSku.name}」卡牌比例配置`}
                extra={
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => generateSingleStickerPDF(selectedSku)}
                    loading={stickerGenerating}
                  >
                    生成贴纸PDF
                  </Button>
                }
              >
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col><Statistic title="每包张数" value={selectedSku.packSize} /></Col>
                  <Col><Statistic title="批次总卡数" value={totalCardsInBatch} /></Col>
                  <Col>
                    <Statistic
                      title="可生产包数"
                      value={maxPacksFull}
                      suffix={maxPacksRemainder > 0 ? `包 +${maxPacksRemainder}张` : '包'}
                    />
                  </Col>
                </Row>
                {(() => {
                  // ---- 规则冲突检测 ----
                  const totalAllLocal = cards.reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                  const totalPacksLocal = selectedSku && selectedSku.packSize > 0
                    ? Math.floor(totalAllLocal / selectedSku.packSize) : 0;

                  const ruleConflicts = rules.map(rule => {
                    const rarityTotal = cards
                      .filter(c => c.rarity === rule.rarity)
                      .reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                    const avgPerPack = totalPacksLocal > 0 ? rarityTotal / totalPacksLocal : 0;
                    let conflict = false;
                    if (totalPacksLocal > 0) {
                      if (rule.operator === 'exactly') conflict = rarityTotal !== rule.count * totalPacksLocal;
                      else if (rule.operator === 'at_least') conflict = rarityTotal < rule.count * totalPacksLocal;
                      else if (rule.operator === 'at_most') conflict = rarityTotal > rule.count * totalPacksLocal;
                    }
                    return { ...rule, avgPerPack, conflict };
                  });
                  const hasConflict = ruleConflicts.some(r => r.conflict);
                  const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));

                  return (
                    <>
                      <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>包装规则</Divider>
                      {hasConflict && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 8 }}
                          message="当前配比与规则存在冲突，请调整数量"
                        />
                      )}
                      {ruleConflicts.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          {ruleConflicts.map(rule => (
                            <Row key={rule.id} align="middle" style={{ marginBottom: 4 }} gutter={8}>
                              <Col flex="auto">
                                <Space size={4}>
                                  {rule.conflict
                                    ? <Text type="warning">⚠</Text>
                                    : <Text type="success">✓</Text>}
                                  <Text>每包{OPERATOR_LABELS[rule.operator]}</Text>
                                  <Text strong>{rule.count}</Text>
                                  <Text>张</Text>
                                  <Tag color={getRarityColor(rule.rarity)} style={{ margin: 0 }}>{rule.rarity}</Tag>
                                  <Text>卡</Text>
                                  {totalPacksLocal > 0 && (
                                    <Text type={rule.conflict ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                                      （当前约 {rule.avgPerPack % 1 === 0 ? rule.avgPerPack : rule.avgPerPack.toFixed(2)} 张/包）
                                    </Text>
                                  )}
                                </Space>
                              </Col>
                              <Col>
                                <Popconfirm title="确认删除？" onConfirm={() => handleDeleteRule(rule.id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      )}
                      <Form form={ruleForm} layout="inline" onFinish={handleUpsertRule} style={{ marginBottom: 12 }}>
                        <Form.Item name="rarity" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select
                            size="small"
                            placeholder="稀有度"
                            style={{ width: 80 }}
                            showSearch
                            options={uniqueRarities.map(r => ({ value: r, label: r }))}
                          />
                        </Form.Item>
                        <Form.Item name="operator" initialValue="exactly" style={{ marginBottom: 0 }}>
                          <Select size="small" style={{ width: 72 }}>
                            <Select.Option value="exactly">恰好</Select.Option>
                            <Select.Option value="at_least">至少</Select.Option>
                            <Select.Option value="at_most">最多</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="count" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <InputNumber size="small" min={1} style={{ width: 60 }} placeholder="张数" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Button size="small" type="dashed" htmlType="submit" icon={<PlusOutlined />}>添加规则</Button>
                        </Form.Item>
                      </Form>
                    </>
                  );
                })()}

                {(() => {
                  const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));
                  if (uniqueRarities.length === 0) return null;
                  const localTotal = cards.reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                  return (
                    <>
                      <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>按稀有度批量设置数量</Divider>
                      <Row gutter={[12, 8]} style={{ marginBottom: 12 }} wrap>
                        {uniqueRarities.map(rarity => {
                          const rarityTotal = cards
                            .filter(c => c.rarity === rarity)
                            .reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                          const prob = localTotal > 0
                            ? (rarityTotal / localTotal * 100).toFixed(1)
                            : null;
                          return (
                          <Col key={rarity}>
                            <Space size={4}>
                              <Tag color={getRarityColor(rarity)} style={{ margin: 0, minWidth: 36, textAlign: 'center' }}>{rarity}</Tag>
                              <InputNumber
                                min={1}
                                size="small"
                                style={{ width: 70 }}
                                placeholder="数量"
                                value={ratioQuickValues[rarity]}
                                onChange={val => setRatioQuickValues(prev => ({ ...prev, [rarity]: val ?? undefined }))}
                              />
                              <Button
                                size="small"
                                type="dashed"
                                loading={ratioQuickLoading}
                                disabled={!ratioQuickValues[rarity]}
                                onClick={() => handleRarityRatioFill(rarity)}
                              >
                                填入全部 {rarity}
                              </Button>
                              {prob !== null && (
                                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                  {rarityTotal}张 / 占{prob}%
                                </Text>
                              )}
                            </Space>
                          </Col>
                          );
                        })}
                      </Row>
                    </>
                  );
                })()}
                <Table rowKey="id" dataSource={cards} columns={ratioColumns} pagination={false} size="small" className="ratio-table" />

                {batches.length > 0 && (
                  <>
                    <Divider orientation="left" plain style={{ margin: '12px 0 8px' }}>最近批次</Divider>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={batches.slice(0, 10)}
                      columns={[
                        { title: 'ID', dataIndex: 'id', width: 60 },
                        {
                          title: '状态', dataIndex: 'status', width: 80,
                          render: (v: string) => {
                            const map: Record<string, string> = { pending: 'default', in_progress: 'processing', completed: 'success', cancelled: 'error' };
                            const label: Record<string, string> = { pending: '待生产', in_progress: '生产中', completed: '已完成', cancelled: '已取消' };
                            return <Badge status={map[v] as any} text={label[v] ?? v} />;
                          },
                        },
                        {
                          title: '进度', key: 'progress', width: 100,
                          render: (_: any, r: Batch) => `${r.completedPacks} / ${r.totalPacks}`,
                        },
                        { title: '备注', dataIndex: 'notes', ellipsis: true },
                        {
                          title: '创建时间', dataIndex: 'created', width: 150,
                          render: (v: string) => new Date(v).toLocaleString('zh-CN'),
                        },
                        {
                          title: '操作', width: 80,
                          render: (_: any, r: Batch) => (
                            <Button size="small" loading={batchPacksLoading} onClick={() => openBatchPacks(r)}>查看序列</Button>
                          ),
                        },
                      ]}
                    />
                  </>
                )}
              </Card>
            ) : (
              <Alert message="请从左侧选择SKU以配置卡牌比例" type="info" showIcon />
            )}
          </Col>
        </Row>
      )}

      {/* Batch Packs Modal */}
      {batchPacksModal && (() => {
        const cardMap = new Map(cards.map(c => [c.id, c]));

        const getPackViolations = (pack: Pack) => {
          const rarityCounts = new Map<string, number>();
          pack.cardIds.forEach(id => {
            const rarity = cardMap.get(id)?.rarity;
            if (rarity) rarityCounts.set(rarity, (rarityCounts.get(rarity) ?? 0) + 1);
          });
          return rules.filter(rule => {
            const count = rarityCounts.get(rule.rarity) ?? 0;
            if (rule.operator === 'exactly') return count !== rule.count;
            if (rule.operator === 'at_most') return count > rule.count;
            if (rule.operator === 'at_least') return count < rule.count;
            return false;
          });
        };

        const packsWithViolations = batchPacksModal.packs.map(p => ({ ...p, violations: getPackViolations(p) }));
        const totalViolated = packsWithViolations.filter(p => p.violations.length > 0).length;

        const selectedPacks = packsWithViolations.filter(p => selectedPackKeys.includes(p.id));
        const selTotalCards = selectedPacks.reduce((s, p) => s + p.cardIds.length, 0);
        const selUniqueCards = new Set(selectedPacks.flatMap(p => p.cardIds)).size;
        const selRarityCounts = new Map<string, number>();
        selectedPacks.forEach(p => p.cardIds.forEach(id => {
          const rarity = cardMap.get(id)?.rarity;
          if (rarity) selRarityCounts.set(rarity, (selRarityCounts.get(rarity) ?? 0) + 1);
        }));

        return (
          <Modal
            title={`批次 #${batchPacksModal.batch.id} 序列（共 ${batchPacksModal.packs.length} 包）`}
            open
            onCancel={() => { setBatchPacksModal(null); setSelectedPackKeys([]); }}
            footer={null}
            width={820}
          >
            {totalViolated > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 10 }}
                message={`发现 ${totalViolated} 包不满足规则`}
                description={rules.map(r => `每包${OPERATOR_LABELS[r.operator]} ${r.count} 张 ${r.rarity}`).join('，')}
              />
            )}
            {totalViolated === 0 && rules.length > 0 && (
              <Alert type="success" showIcon style={{ marginBottom: 10 }} message="所有包均满足规则" />
            )}

            {selectedPackKeys.length > 0 && (
              <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
                <Space size={16} wrap>
                  <Text>已选 <Text strong>{selectedPackKeys.length}</Text> 包</Text>
                  <Text>合计 <Text strong>{selTotalCards}</Text> 张</Text>
                  <Text>不重复 <Text strong>{selUniqueCards}</Text> 张</Text>
                  {Array.from(selRarityCounts.entries()).map(([rarity, count]) => (
                    <Space key={rarity} size={2}>
                      <Tag color={getRarityColor(rarity)} style={{ margin: 0 }}>{rarity}</Tag>
                      <Text strong>{count}</Text>
                      <Text type="secondary">张</Text>
                    </Space>
                  ))}
                </Space>
              </div>
            )}

            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              dataSource={packsWithViolations}
              rowSelection={{
                selectedRowKeys: selectedPackKeys,
                onChange: keys => setSelectedPackKeys(keys as number[]),
              }}
              columns={[
                { title: '包号', dataIndex: 'packNumber', width: 55 },
                {
                  title: '已生产', dataIndex: 'isProduced', width: 72,
                  render: (v: boolean) => v ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
                },
                {
                  title: '规则', key: 'violations', width: 62,
                  render: (_: any, r: any) => r.violations.length > 0
                    ? (
                      <Tooltip title={r.violations.map((v: any) => `${v.rarity} 每包${OPERATOR_LABELS[v.operator]}${v.count}张`).join('；')}>
                        <Tag color="red">违规</Tag>
                      </Tooltip>
                    )
                    : <Tag color="green">✓</Tag>,
                },
                {
                  title: '卡牌序列', dataIndex: 'cardIds', ellipsis: false,
                  render: (ids: number[], r: Pack) => (
                    <Space size={4} wrap>
                      {(ids ?? []).map((id, i) => {
                        const c = cardMap.get(id);
                        return (
                          <Tooltip key={i} title={c ? `${c.name}（${c.rarity}）` : undefined}>
                            <Tag color={c ? getRarityColor(c.rarity) : undefined} style={{ margin: 0 }}>
                              {r.cardNumbers?.[i] ?? id}
                            </Tag>
                          </Tooltip>
                        );
                      })}
                    </Space>
                  ),
                },
              ]}
            />
          </Modal>
        );
      })()}

      {/* SKU Modal */}
      <Modal title={editingSku ? '编辑SKU' : '新增SKU'} open={skuModalVisible} onCancel={() => setSkuModalVisible(false)} onOk={() => skuForm.submit()} okText="保存">
        <Form form={skuForm} layout="vertical" onFinish={handleSkuSubmit}>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="code" label="SKU编码" rules={[{ required: true, message: '编码必填' }]}>
                <Input placeholder="如: R180" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="name" label="SKU名称" rules={[{ required: true }]}>
                <Input placeholder="如: 黄金版、白银版" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="packSize" label="每包张数" rules={[{ required: true }]} initialValue={5}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图">
            <Input placeholder="https://..." style={{ marginBottom: 8 }} />
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.thumbnailUrl !== cur.thumbnailUrl || prev.code !== cur.code}>
              {({ getFieldValue, setFieldValue }) => {
                const url = getFieldValue('thumbnailUrl');
                return (
                  <Space align="start">
                    {url && <img src={`${url}?x-oss-process=image/resize,w_200`} alt="缩略图" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }} />}
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        const code = getFieldValue('code');
                        if (!code) { message.warning('请先填写SKU编码'); return false; }
                        setSkuThumbUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('skuCode', code);
                          fd.append('file', file);
                          const res = await fetch(`${BASE_URL}/skus/upload-thumbnail`, { method: 'POST', body: fd });
                          const json = await res.json();
                          if (json.success) {
                            setFieldValue('thumbnailUrl', json.data.url);
                            message.success('上传成功');
                          } else {
                            message.error(json.message);
                          }
                        } catch (e: any) {
                          message.error(e.message);
                        } finally {
                          setSkuThumbUploading(false);
                        }
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={skuThumbUploading} size="small">上传图片</Button>
                    </Upload>
                  </Space>
                );
              }}
            </Form.Item>
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

    </Card>
  );
};

// ==================== Restock Alerts Tab ====================

const RestockAlertsTab: React.FC = () => {
  const [groups, setGroups] = useState<Array<{ set: CardSet; cards: CardWithInventory[] }>>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/card-inventory/alerts`);
      const json = await res.json();
      if (json.success) setGroups(json.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCritical = groups.reduce((s, g) => s + g.cards.filter(c => c.isCriticalStock).length, 0);
  const totalLow = groups.reduce((s, g) => s + g.cards.filter(c => c.isLowStock && !c.isCriticalStock).length, 0);

  const columns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    { title: '稀有度', dataIndex: 'rarity', width: 90, render: (v: string) => <Tag color={getRarityColor(v)}>{v}</Tag> },
    { title: '库存(张)', dataIndex: 'stock', width: 90, render: (v: number, r: CardWithInventory) => <span className={r.isCriticalStock ? 'stock-critical' : 'stock-warning'} style={{ fontWeight: 600 }}>{v}</span> },
    { title: '月消耗', dataIndex: 'monthlyConsumption', width: 80 },
    { title: '预计余量', dataIndex: 'monthsRemaining', width: 90, render: (v: number | null) => v != null ? `${v.toFixed(1)} 月` : '-' },
    { title: '建议补货', dataIndex: 'restockSuggestion', width: 100, render: (v: number) => <Text type="danger" strong>{v} 张</Text> },
    {
      title: '紧急程度', width: 100,
      render: (_: any, r: CardWithInventory) =>
        r.isCriticalStock ? <Tag color="red">严重不足</Tag> : <Tag color="orange">需补货</Tag>,
    },
  ];

  if (!loading && groups.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#52c41a' }}>
          <CheckCircleOutlined style={{ fontSize: 48, marginBottom: 12 }} />
          <div style={{ fontSize: 18, fontWeight: 600 }}>所有卡牌库存充足</div>
          <div style={{ color: '#999', marginTop: 8 }}>暂无需要补货的卡牌</div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <span>补货预警</span>
          {totalCritical > 0 && <Badge count={totalCritical} style={{ backgroundColor: '#ff4d4f' }} />}
          {totalLow > 0 && <Badge count={totalLow} style={{ backgroundColor: '#faad14' }} />}
        </Space>
      }
      extra={<Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>}
    >
      {totalCritical > 0 && <Alert type="error" showIcon icon={<WarningOutlined />} className="inventory-alert"
        message={`共 ${totalCritical} 张卡牌库存严重不足，请立即补货！`} />}
      {totalLow > 0 && <Alert type="warning" showIcon className="inventory-alert"
        message={`共 ${totalLow} 张卡牌库存不足1个月，建议尽快安排补货。`} />}

      {groups.map(({ set, cards }) => (
        <Card key={set.id} size="small" title={`${set.code} ${set.name}`}
          style={{ marginBottom: 16 }}
          extra={<Text type="secondary">需补货 {cards.length} 张</Text>}
        >
          <Table
            rowKey="id"
            dataSource={cards}
            columns={columns}
            pagination={false}
            size="small"
            loading={loading}
          />
        </Card>
      ))}
    </Card>
  );
};

// ==================== Production Tasks Tab ====================

interface ProductionTaskSkuEntry {
  skuId: number;
  packCount: number;
  name: string;
  batchId: number | null;
  status: 'pending' | 'active' | 'completed';
}

interface ProductionTask {
  id: number;
  cardSetId: number;
  name: string;
  skuEntries: ProductionTaskSkuEntry[];
  ratioSnapshot: { skus: Record<number, any> };
  packSequences: Record<number, number[][]> | null;
  procurementData: {
    generatedAt: string;
    entries: Array<{
      skuId: number;
      skuName: string;
      skuCode: string;
      packCount: number;
      cards: Array<{ cardId: number; cardNumber: string; name: string; rarity: string; count: number }>;
    }>;
    totals: Array<{
      cardId: number;
      cardNumber: string;
      name: string;
      rarity: string;
      totalCount: number;
      bySku: Record<number, number>;
    }>;
  } | null;
  status: 'draft' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  cardSet?: CardSet;
  created: string;
}

const TASK_STATUS_MAP: Record<string, { color: string; label: string }> = {
  draft: { color: 'default', label: '草稿' },
  confirmed: { color: 'blue', label: '已确认' },
  in_progress: { color: 'processing', label: '生产中' },
  completed: { color: 'success', label: '已完成' },
  cancelled: { color: 'error', label: '已取消' },
};

const ProductionTasksTab: React.FC<{ sets: CardSet[] }> = ({ sets }) => {
  const [tasks, setTasks] = useState<ProductionTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProductionTask | null>(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<ProductionTask | null>(null);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [taskForm] = Form.useForm();
  const [taskSetId, setTaskSetId] = useState<number | null>(null);
  const [taskSkus, setTaskSkus] = useState<any[]>([]);
  // Dynamic SKU entry list for the modal
  const [entryList, setEntryList] = useState<Array<{ skuId: number | null; packCount: number | null; name: string }>>([
    { skuId: null, packCount: null, name: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  // Activating state
  const [activatingSkuId, setActivatingSkuId] = useState<number | null>(null);
  const [activatingAll, setActivatingAll] = useState(false);

  // Card inventory map for procurement display
  const [cardInventoryMap, setCardInventoryMap] = useState<Record<number, { stock: number }>>({});

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/production-tasks`);
      const json = await res.json();
      if (json.success) setTasks(json.data);
    } catch (e: any) {
      message.error('加载任务列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const loadTaskDetail = useCallback(async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${id}`);
      const json = await res.json();
      if (json.success) {
        setSelectedTaskDetail(json.data);
        if (json.data.procurementData) {
          loadCardInventoryForTask(json.data);
        }
      }
    } catch (e: any) {
      message.error('加载任务详情失败');
    }
  }, []);

  const loadCardInventoryForTask = async (task: ProductionTask) => {
    if (!task.cardSetId) return;
    try {
      const res = await fetch(`${BASE_URL}/card-inventory?setId=${task.cardSetId}`);
      const json = await res.json();
      if (json.success) {
        const map: Record<number, { stock: number }> = {};
        (json.data as any[]).forEach((c: any) => { map[c.id] = { stock: c.stock ?? 0 }; });
        setCardInventoryMap(map);
      }
    } catch { /* ignore */ }
  };

  const handleSelectTask = (task: ProductionTask) => {
    setSelectedTask(task);
    loadTaskDetail(task.id);
  };

  // ---- Modal: Create Task ----
  const openCreateModal = () => {
    setTaskSetId(null);
    setTaskSkus([]);
    setEntryList([{ skuId: null, packCount: null, name: '' }]);
    taskForm.resetFields();
    setModalVisible(true);
  };

  const handleSetChange = async (setId: number) => {
    setTaskSetId(setId);
    setEntryList([{ skuId: null, packCount: null, name: '' }]);
    try {
      const res = await fetch(`${BASE_URL}/skus?setId=${setId}`);
      const json = await res.json();
      if (json.success) setTaskSkus(json.data);
    } catch { setTaskSkus([]); }
  };

  const handleCreateTask = async () => {
    const name = taskForm.getFieldValue('name');
    if (!name) { message.warning('请输入任务名称'); return; }
    if (!taskSetId) { message.warning('请选择系列'); return; }
    const validEntries = entryList.filter(e => e.skuId && e.packCount && e.packCount > 0);
    if (validEntries.length === 0) { message.warning('请至少配置一个 SKU 条目'); return; }

    // Check for duplicate skuIds
    const skuIds = validEntries.map(e => e.skuId);
    if (new Set(skuIds).size !== skuIds.length) {
      message.warning('同一个 SKU 不能重复添加');
      return;
    }

    setSubmitting(true);
    try {
      // Find SKU names and build entries
      const skuMap = new Map(taskSkus.map((s: any) => [s.id, s]));
      const skuEntries = validEntries.map(e => ({
        skuId: e.skuId!,
        packCount: e.packCount!,
        name: e.name || `${(skuMap.get(e.skuId!) as any)?.code || 'SKU'} ${e.packCount}包`,
      }));

      const res = await fetch(`${BASE_URL}/production-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardSetId: taskSetId,
          name,
          skuEntries,
          notes: taskForm.getFieldValue('notes') || undefined,
        }),
      });
      const json = await res.json();
      if (json.success) {
        message.success('任务创建成功');
        setModalVisible(false);
        loadTasks();
        handleSelectTask(json.data);
      } else {
        message.error(json.message || '创建失败');
      }
    } catch (e: any) {
      message.error('创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Calculate Procurement ----
  const handleCalculate = async (taskId: number) => {
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${taskId}/calculate`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        message.success('采购需求计算完成');
        loadTasks();
        loadTaskDetail(taskId);
      } else {
        message.error(json.message || '计算失败');
      }
    } catch (e: any) {
      message.error('计算失败');
    }
  };

  // ---- Activate SKU Entry ----
  const handleActivate = async (taskId: number, skuId: number) => {
    setActivatingSkuId(skuId);
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${taskId}/activate/${skuId}`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        message.success(`SKU 条目已激活，批次 #${json.data.batchId}`);
        loadTasks();
        loadTaskDetail(taskId);
      } else {
        message.error(json.message || '激活失败');
      }
    } catch (e: any) {
      message.error('激活失败');
    } finally {
      setActivatingSkuId(null);
    }
  };

  // ---- Activate All ----
  const handleActivateAll = async (taskId: number) => {
    setActivatingAll(true);
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${taskId}/activate-all`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        const results = json.data as any[];
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        if (failCount > 0) {
          message.warning(`${successCount} 个成功，${failCount} 个失败`);
        } else {
          message.success(`全部 ${successCount} 个 SKU 条目已激活`);
        }
        loadTasks();
        loadTaskDetail(taskId);
      } else {
        message.error(json.message || '激活失败');
      }
    } catch (e: any) {
      message.error('激活失败');
    } finally {
      setActivatingAll(false);
    }
  };

  // ---- Delete Task ----
  const handleDeleteTask = async (taskId: number) => {
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${taskId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        message.success('任务已删除');
        setSelectedTask(null);
        setSelectedTaskDetail(null);
        loadTasks();
      } else {
        message.error(json.message || '删除失败');
      }
    } catch (e: any) {
      message.error('删除失败');
    }
  };

  // ---- Cancel Task ----
  const handleCancelTask = async (taskId: number) => {
    try {
      const res = await fetch(`${BASE_URL}/production-tasks/${taskId}/cancel`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        message.success('任务已取消');
        loadTasks();
        loadTaskDetail(taskId);
      } else {
        message.error(json.message || '取消失败');
      }
    } catch (e: any) {
      message.error('取消失败');
    }
  };

  // ---- Task List Columns ----
  const taskColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '任务名称', dataIndex: 'name', ellipsis: true },
    {
      title: '系列', key: 'cardSet', width: 100,
      render: (_: any, r: ProductionTask) => r.cardSet ? `${r.cardSet.code || ''} ${r.cardSet.name}` : '-',
    },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v: string) => {
        const m = TASK_STATUS_MAP[v];
        return m ? <Badge status={m.color as any} text={m.label} /> : v;
      },
    },
    {
      title: 'SKU 条目', key: 'skuEntries', width: 140,
      render: (_: any, r: ProductionTask) => {
        const entries = r.skuEntries || [];
        const active = entries.filter((e: any) => e.status === 'active' || e.status === 'completed').length;
        return `${entries.length} 个 SKU / ${active} 已启动`;
      },
    },
    {
      title: '创建时间', dataIndex: 'created', width: 150,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
  ];

  // ---- Render: Task Detail ----
  const renderTaskDetail = () => {
    const task = selectedTaskDetail;
    if (!task) return <Alert message="请从左侧选择生产任务" type="info" showIcon />;

    const entries = task.skuEntries || [];
    const totalPacks = entries.reduce((s, e) => s + e.packCount, 0);
    const statusInfo = TASK_STATUS_MAP[task.status];

    // Compute SKU meta info from ratioSnapshot
    const skuMetas: Record<number, { maxPacks: number; code: string }> = {};
    if (task.ratioSnapshot?.skus) {
      for (const [skuIdStr, skuData] of Object.entries(task.ratioSnapshot.skus)) {
        const sd: any = skuData;
        const totalCards = (sd.ratios as any[]).reduce((s: number, r: any) => s + r.quantity, 0);
        skuMetas[parseInt(skuIdStr)] = {
          maxPacks: Math.floor(totalCards / sd.packSize),
          code: '',
        };
      }
    }

    return (
      <div>
        {/* Task Info */}
        <Card size="small" title={`任务 #${task.id}：${task.name}`} style={{ marginBottom: 12 }}>
          <Row gutter={16}>
            <Col>
              <Statistic title="系列" value={task.cardSet ? `${task.cardSet.code || ''} ${task.cardSet.name}` : '-'} />
            </Col>
            <Col>
              <Statistic title="状态" valueRender={() => <Badge status={statusInfo.color as any} text={statusInfo.label} />} />
            </Col>
            <Col>
              <Statistic title="总包数" value={totalPacks} suffix="包" />
            </Col>
            <Col>
              <Statistic title="SKU 数" value={entries.length} />
            </Col>
          </Row>
          {task.notes && <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>备注：{task.notes}</Text>}
          <div style={{ marginTop: 8 }}>
            <Space>
              {task.status === 'draft' && (
                <>
                  <Button type="primary" size="small" onClick={() => handleCalculate(task.id)}>计算采购需求</Button>
                  <Popconfirm title="确认删除？" onConfirm={() => handleDeleteTask(task.id)}>
                    <Button size="small" danger>删除</Button>
                  </Popconfirm>
                </>
              )}
              {task.status === 'confirmed' && (
                <>
                  <Button type="primary" size="small" loading={activatingAll} onClick={() => handleActivateAll(task.id)}>全部启动</Button>
                  <Popconfirm title="确认取消任务？" onConfirm={() => handleCancelTask(task.id)} disabled={activatingAll}>
                    <Button size="small" danger disabled={activatingAll}>取消任务</Button>
                  </Popconfirm>
                </>
              )}
              {task.status === 'in_progress' && (
                <>
                  <Button size="small" loading={activatingAll} onClick={() => handleActivateAll(task.id)}>启动剩余条目</Button>
                  <Popconfirm title="将取消所有未生产的 SKU 条目，确认？" onConfirm={() => handleCancelTask(task.id)} disabled={activatingAll}>
                    <Button size="small" danger disabled={activatingAll}>取消任务</Button>
                  </Popconfirm>
                </>
              )}
              {task.status === 'completed' && <Tag color="success">全部完成</Tag>}
              {task.status === 'cancelled' && <Tag color="error">已取消</Tag>}
            </Space>
          </div>
        </Card>

        {/* SKU Entries Table */}
        <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>SKU 配置</Divider>
        <Table
          rowKey="skuId"
          size="small"
          pagination={false}
          dataSource={entries}
          style={{ marginBottom: 12 }}
          columns={[
            { title: '名称', dataIndex: 'name' },
            {
              title: '计划包数', dataIndex: 'packCount', width: 100,
              render: (v: number, r: ProductionTaskSkuEntry) => {
                const meta = skuMetas[r.skuId];
                const maxP = meta?.maxPacks;
                return <Text>{v}{maxP !== undefined ? <Text type="secondary"> / {maxP}</Text> : ''}</Text>;
              },
            },
            {
              title: '状态', dataIndex: 'status', width: 100,
              render: (v: string) => {
                const map: Record<string, { color: string; text: string }> = {
                  pending: { color: 'default', text: '待激活' },
                  active: { color: 'processing', text: '生产中' },
                  completed: { color: 'success', text: '已完成' },
                };
                const m = map[v] || { color: 'default', text: v };
                return <Badge status={m.color as any} text={m.text} />;
              },
            },
            {
              title: '关联批次', dataIndex: 'batchId', width: 80,
              render: (v: number | null) => v ? <a>#{v}</a> : '-',
            },
            {
              title: '操作', width: 100,
              render: (_: any, entry: ProductionTaskSkuEntry) => {
                if (entry.status !== 'pending') return null;
                if (!['confirmed', 'in_progress'].includes(task.status)) return null;
                return (
                  <Button
                    size="small"
                    type="primary"
                    loading={activatingSkuId === entry.skuId}
                    disabled={activatingAll || activatingSkuId !== null}
                    onClick={() => handleActivate(task.id, entry.skuId)}
                  >
                    启动生产
                  </Button>
                );
              },
            },
          ]}
        />

        {/* Procurement Data: Aggregated Table */}
        {task.procurementData && (
          <>
            <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>
              采购需求清单（汇总）
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                {task.procurementData.generatedAt ? new Date(task.procurementData.generatedAt).toLocaleString('zh-CN') : '-'}
              </Text>
            </Divider>

            {/* Summary stats */}
            {(() => {
              const totals = task.procurementData.totals;
              let insufficientCount = 0;
              let totalNeeded = 0;
              totals.forEach(t => {
                totalNeeded += t.totalCount;
                const inv = cardInventoryMap[t.cardId];
                if (inv && inv.stock < t.totalCount) insufficientCount++;
              });
              return (
                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col><Statistic title="需采购卡种" value={totals.length} /></Col>
                  <Col>
                    <Statistic title="库存不足" value={insufficientCount}
                      valueStyle={{ color: insufficientCount > 0 ? '#ff4d4f' : '#52c41a' }} />
                  </Col>
                  <Col>
                    <Statistic title="需采购总张数" value={totalNeeded} />
                  </Col>
                </Row>
              );
            })()}

            {/* Filter: show/hide sufficient cards */}
            <Table
              rowKey="cardId"
              size="small"
              pagination={false}
              scroll={{ x: 500 + (task.procurementData.entries.length * 90) }}
              dataSource={task.procurementData.totals.map(t => {
                const inv = cardInventoryMap[t.cardId];
                const stock = inv?.stock ?? 0;
                return {
                  ...t,
                  _stock: stock,
                  _gap: Math.max(0, t.totalCount - stock),
                };
              })}
              columns={[
                { title: '编号', dataIndex: 'cardNumber', width: 80, fixed: 'left' as any },
                { title: '名称', dataIndex: 'name', width: 120, fixed: 'left' as any, ellipsis: true },
                {
                  title: '稀有度', dataIndex: 'rarity', width: 70, fixed: 'left' as any,
                  render: (v: string) => <Tag color={getRarityColor(v)} style={{ fontSize: 11 }}>{v}</Tag>,
                },
                ...task.procurementData.entries.map(entry => ({
                  title: <span style={{ fontSize: 11 }}>{entry.skuCode || entry.skuName}</span>,
                  width: 80,
                  render: (_: any, row: any) => {
                    const v = row.bySku?.[entry.skuId] ?? 0;
                    return v > 0 ? v : <Text type="secondary">-</Text>;
                  },
                })),
                {
                  title: '合计', width: 70,
                  render: (_: any, row: any) => <Text strong>{row.totalCount}</Text>,
                },
                {
                  title: '库存', dataIndex: '_stock', width: 70,
                },
                {
                  title: '缺口', dataIndex: '_gap', width: 70,
                  render: (v: number) => v > 0 ? <Text type="danger" strong>{v}</Text> : <Text type="success">0</Text>,
                },
              ]}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <Row gutter={16}>
      <Col span={8}>
        <Card
          size="small"
          title="生产任务列表"
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} size="small" onClick={loadTasks} />
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateModal}>新建任务</Button>
            </Space>
          }
        >
          <Table
            rowKey="id"
            dataSource={tasks}
            columns={taskColumns}
            loading={loading}
            pagination={false}
            size="small"
            rowClassName={r => r.id === selectedTask?.id ? 'ant-table-row-selected' : ''}
            onRow={r => ({ onClick: () => handleSelectTask(r), style: { cursor: 'pointer' } })}
          />
        </Card>

        {/* Create Task Modal */}
        <Modal
          title="新建生产任务"
          open={modalVisible}
          onOk={handleCreateTask}
          onCancel={() => setModalVisible(false)}
          confirmLoading={submitting}
          width={700}
          destroyOnClose
        >
          <Form form={taskForm} layout="vertical">
            <Form.Item label="选择系列" required>
              <Select
                placeholder="选择卡牌系列"
                value={taskSetId}
                onChange={(v: number) => handleSetChange(v)}
                options={sets.map(s => ({ value: s.id, label: `${s.code} ${s.name}` }))}
              />
            </Form.Item>
            <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
              <Input placeholder="如：5月生产计划" />
            </Form.Item>
            <Form.Item name="notes" label="备注">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Divider plain>SKU 配置（添加需要生产的 SKU 并设置包数）</Divider>
            {entryList.map((entry, idx) => (
              <Row key={idx} gutter={8} style={{ marginBottom: 8 }} align="middle">
                <Col flex="auto">
                  <Select
                    size="small"
                    placeholder="选择 SKU"
                    value={entry.skuId}
                    disabled={!taskSetId}
                    onChange={(v: number) => {
                      const list = [...entryList];
                      list[idx] = { ...list[idx], skuId: v };
                      // Auto-fill name
                      const sku = taskSkus.find((s: any) => s.id === v);
                      if (sku && !list[idx].name) {
                        list[idx].name = `${(sku as any).code || sku.name} ${list[idx].packCount || '?'}包`;
                      }
                      setEntryList(list);
                    }}
                    options={taskSkus.map((s: any) => ({
                      value: s.id,
                      label: `${(s as any).code || ''} ${s.name} (${s.packSize}张/包)`,
                      disabled: entryList.some((e, i) => i !== idx && e.skuId === s.id),
                    }))}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col style={{ width: 100 }}>
                  <InputNumber
                    size="small"
                    min={1}
                    placeholder="包数"
                    value={entry.packCount}
                    onChange={v => {
                      const list = [...entryList];
                      list[idx] = { ...list[idx], packCount: v };
                      if (!list[idx].name && list[idx].skuId) {
                        const sku = taskSkus.find((s: any) => s.id === list[idx].skuId);
                        if (sku) list[idx].name = `${(sku as any).code || sku.name} ${v || '?'}包`;
                      }
                      setEntryList(list);
                    }}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col flex="auto">
                  <Input
                    size="small"
                    placeholder="条目名称（可选）"
                    value={entry.name}
                    onChange={e => {
                      const list = [...entryList];
                      list[idx] = { ...list[idx], name: e.target.value };
                      setEntryList(list);
                    }}
                  />
                </Col>
                <Col>
                  <Button
                    size="small" type="text" danger icon={<DeleteOutlined />}
                    onClick={() => setEntryList(entryList.filter((_, i) => i !== idx))}
                    disabled={entryList.length <= 1}
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed" size="small" icon={<PlusOutlined />}
              onClick={() => setEntryList([...entryList, { skuId: null, packCount: null, name: '' }])}
              block
            >
              添加 SKU 条目
            </Button>
            {(() => {
              const valid = entryList.filter(e => e.skuId && e.packCount && e.packCount > 0);
              const totalRequested = valid.reduce((s, e) => s + (e.packCount || 0), 0);
              return (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Text type="secondary">
                    配置 {valid.length} 个 SKU 条目，总包数：<Text strong>{totalRequested}</Text>
                  </Text>
                </div>
              );
            })()}
          </Form>
        </Modal>
      </Col>
      <Col span={16}>
        {renderTaskDetail()}
      </Col>
    </Row>
  );
};

// ==================== Main Component ====================

const CardMakerAdmin: React.FC = () => {
  const [sets, setSets] = useState<CardSet[]>([]);
  const [activeTab, setActiveTab] = useState('sets');

  const loadSets = async () => {
    const res = await fetch(`${BASE_URL}/sets`);
    const json = await res.json();
    if (json.success) setSets(json.data);
  };

  useEffect(() => { loadSets(); }, []);

  return (
    <div className="card-maker-admin">
      <Title level={3}>卡牌制作管理</Title>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="卡牌系列" key="sets">
          <CardSetsTab onSetChange={loadSets} />
        </TabPane>
        <TabPane tab="卡牌管理" key="cards">
          <CardsTab sets={sets} />
        </TabPane>
        <TabPane tab="SKU管理" key="skus">
          <SkuTab sets={sets} />
        </TabPane>
        <TabPane tab="补货预警" key="restock">
          <RestockAlertsTab />
        </TabPane>
        <TabPane tab="生产任务" key="tasks">
          <ProductionTasksTab sets={sets} />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default CardMakerAdmin;
